import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { PersonaTipoUtenteEnum } from '../../api';
import { PraticaSelf } from '../../api/generated/dob';
import {
  commonErrorMessages,
  homeCompilationSelectionDialogMessages,
  homeSpecimenUploadDialogMessages,
  walletPageMessages,
} from '../../messages';
import {
  ApiRequestStatus,
  DashboardWalletSummary,
  FinancialAdvisor,
  GlobalActions,
  News,
  Questionnaire,
  SurveyBookingStatus,
  TabItem,
  WalletSections,
} from '../../model';
import { appRoutes } from '../../routing';
import {
  downloadFinancialAdvisorProfile,
  downloadHomeData,
  getTasks,
  homeActions,
  HomeCard,
  HomeState,
  PirBoxState,
  retrieveHomeWalletSummary,
  retrieveOnboardingSelfPracticeData,
  signatureAreaActions,
  startSigningProcess,
  updateOnboardingCompileType,
  updateQuestionnairesStatus,
  updateShowBalance,
  webCollaborationSliceActions,
} from '../../store';
import {
  dossierSliceActions,
  getDossierFilter,
  messagesForDossierError,
  retrieveDossier,
  selectDossierState,
} from '../../store/data/dossier';
import {
  MifidSurveyState,
  resetMifidSurvey,
  resetSurvey,
  resetUpdateQuestionnairesStatus,
  writeMifidNoProfiling,
} from '../../store/features/home/onboarding-self';
import { createRoom } from '../../store/features/web-collaboration/web-collaboration-thunks';
import { useAppDispatch, useAppSelector } from '../redux';
import { useVideoPageLogic } from '../video';
import { parseISO, isBefore, isSameDay } from 'date-fns';
import {
  GetTasksResponse,
  GetTasksResponseMifidStateEnum,
  VideoResponse,
} from '../../api/generated';

export interface DashboardPageLogic {
  toDoActivities: HomeCard<GetTasksResponse>;
  practiceData: HomeCard<PraticaSelf>;
  myWallet: HomeCard<DashboardWalletSummary>;
  sharedAccounts: HomeCard<DashboardWalletSummary>;
  news: HomeCard<News>;
  signature: HomeCard<number>;
  documents: HomeCard<number>;
  financialAdvisor: HomeCard<FinancialAdvisor>;
  video: HomeCard<VideoResponse>;
  sections: TabItem<WalletSections>[];
  selectedSection: WalletSections;
  dossierRequestStatus: ApiRequestStatus;
  onSelectedSection: (selectedSection: WalletSections) => void;
  setFlagShowBalance: (value: boolean) => void;
  selectionDialogOpen: boolean;
  specimenUploadDialogOpen: boolean;
  confirmSignDialogOpen: boolean;
  openConfirmSignDialog: () => void;
  closeConfirmSignDialog: () => void;
  onConfirmSign: () => void;
  mifidViewDialogOpen: boolean;
  openMifidViewDialog: () => void;
  closeMifidViewDialog: () => void;
  onMifidView: () => void;
  onMifidModify: () => void;
  onModalitySelected: (type: Questionnaire, mode?: SurveyBookingStatus) => void;
  openDialog: (open: boolean) => Promise<void>;
  openSpecimenUploadDialog: (open: boolean) => void;
  handleSpecimenUploadSuccess: () => void;
  startWebCollaborationDialogOpen: boolean;
  startWebCollaboration: () => void;
  onCloseStartWebCollaborationDialog: () => void;
}

export const useDashboardPageLogic = (
  globalActions: GlobalActions
): DashboardPageLogic => {
  const {
    news,
    financialAdvisor,
    documents,
    signature,
    selectedSection,
    myWallet: myWalletCard,
    sharedAccounts: sharedAccountsCard,
    toDoActivities,
    practiceData,
    updateOnboardingCompileTypeRequestStatus,
    startSigningProcessRequestStatus,
  } = useAppSelector((state) => state.features.home as HomeState);

  const { updateQuestionnairesStatusStatus } = useAppSelector(
    (state) => state.features.mifidSurvey as MifidSurveyState
  );

  const { productsSummary } = useAppSelector(
    (state) => state.features.pirbox as PirBoxState
  );

  const { showToast, closeToast, navigate } = globalActions;
  const { videos, isLoadingVideos } = useVideoPageLogic(globalActions);
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const [dossierNumberCallingRequest, setDossierNumberCallingRequest] =
    useState(0);
  const [walletNumberCallingRequest, setWalletNumberCallingRequest] =
    useState(0);

  const user = useAppSelector((state) => state.data?.user);
  const showBalance = useAppSelector(
    (state) => state.data?.user?.showBalance ?? false
  );

  // The balance visibility is a single user preference shared with the wallet page
  const myWallet = useMemo(
    () => withShowBalance(myWalletCard, showBalance),
    [myWalletCard, showBalance]
  );
  const sharedAccounts = useMemo(
    () => withShowBalance(sharedAccountsCard, showBalance),
    [sharedAccountsCard, showBalance]
  );
  const taxCode = useAppSelector((state) => state.data?.user?.taxCode);
  const isProspect = useAppSelector((state) => state.data?.user?.isProspect);
  const tokenRubrica = useAppSelector(
    (state) => state.data?.user?.tokenRubrica
  );

  const roomId = useAppSelector(
    (state) => state.features?.webCollaboration?.roomId
  );

  const codicePratica = practiceData?.value?.codicePratica;
  const compileTypeFromDb = practiceData?.value?.compileType;
  const codicePromoter = practiceData?.value?.codicePromoter;

  const name = useAppSelector((state) => state.data?.user?.name) || '';
  const financialAdvisorName = financialAdvisor?.value
    ? financialAdvisor?.value?.name + ' ' + financialAdvisor?.value?.surname
    : '';

  const latestTokenRubrica = useRef(tokenRubrica);

  useEffect(() => {
    if (
      tokenRubrica !== undefined &&
      tokenRubrica !== latestTokenRubrica.current &&
      dossierNumberCallingRequest > 0
    ) {
      latestTokenRubrica.current = tokenRubrica;
      setDossierNumberCallingRequest(0);
      setWalletNumberCallingRequest(0);
      dispatch(dossierSliceActions.resetState());
    }
  }, [tokenRubrica, dispatch, dossierNumberCallingRequest]);

  const video: HomeCard<VideoResponse> = useMemo(
    () =>
      videos?.featured?.[0]
        ? {
            value: videos?.featured?.[0],
            requestStatus: isLoadingVideos ? 'loading' : 'idle',
          }
        : null,
    [videos, isLoadingVideos]
  );

  const userType: PersonaTipoUtenteEnum = useAppSelector(
    (state) => state.data?.user?.userType
  );

  const token: string | undefined = useAppSelector(
    (state) => state.data?.user?.token
  );
  const codiceCliente: string | undefined = useAppSelector(
    (state) => state.data?.user?.userCode
  );

  const {
    dossierFilter,
    dossierFilterRequestStatus,
    dossierFilterError,
    defaultDossier,
  } = useAppSelector(selectDossierState);

  useEffect(() => {
    dispatch(dossierSliceActions.setDossierFilter(defaultDossier));
  }, [defaultDossier, dispatch]);

  const sections = useMemo<TabItem<WalletSections>[]>(() => {
    const tabs: TabItem<WalletSections>[] = [
      {
        type: 'MY_WALLET',
        label: formatMessage(walletPageMessages.myWalletLabel),
      },
    ];

    if (!isProspect) {
      if (userType === PersonaTipoUtenteEnum.Pf) {
        if (
          getDossierFilter(dossierFilter, 'SHARED_ACCOUNTS')?.dossier?.length >
          0
        ) {
          tabs.splice(1, 0, {
            type: 'SHARED_ACCOUNTS',
            label: formatMessage(walletPageMessages.jointRelationshipsLabel),
          });
        }
        if (productsSummary?.length > 0) {
          tabs.splice(2, 0, {
            type: 'PIR_BOX',
            label: formatMessage(walletPageMessages.pirBoxLabel),
          });
        }
      }
    }

    return tabs;
  }, [formatMessage, userType, dossierFilter, productsSummary]);

  useEffect(() => {
    return () => {
      dispatch(homeActions.partialReset());
    };
  }, [dispatch, token]);

  useEffect(() => {
    if (
      token !== undefined &&
      codiceCliente !== undefined &&
      !(isProspect && !practiceData.value)
    ) {
      dispatch(
        downloadHomeData({
          customerCode: isProspect
            ? practiceData.value.prospectCode
            : codiceCliente,
          bundleCode:
            isProspect && practiceData.value?.processStateCode === 2
              ? practiceData.value.bundleCode
              : undefined,
        })
      );
    }
  }, [
    dispatch,
    codiceCliente,
    token,
    isProspect,
    practiceData.value?.bundleCode,
    practiceData.value?.prospectCode,
    tokenRubrica,
  ]);

  useEffect(() => {
    if (taxCode && isProspect) {
      dispatch(retrieveOnboardingSelfPracticeData());
    }
  }, [dispatch, isProspect, taxCode]);

  useEffect(() => {
    if (
      practiceData.value !== undefined &&
      practiceData.requestStatus === 'succeeded' &&
      !practiceData.value?.flagProfilaturaMifid &&
      toDoActivities.value?.mifidState ===
        GetTasksResponseMifidStateEnum.Completed
    ) {
      dispatch(
        writeMifidNoProfiling({
          idCliente: practiceData.value.codiceCliente,
          idIncarico: practiceData.value.codicePratica,
        })
      );
    }
  }, [practiceData, toDoActivities]);

  useEffect(() => {
    !financialAdvisor?.value &&
      codicePromoter &&
      dispatch(downloadFinancialAdvisorProfile(codicePromoter));
  }, [codicePromoter, financialAdvisor?.value, dispatch]);

  useEffect(() => {
    if (
      token &&
      walletNumberCallingRequest === 0 &&
      dossierFilter &&
      dossierFilter.dossier.length > 0 &&
      (!myWalletCard || !myWalletCard.value) &&
      myWalletCard?.requestStatus !== 'loading' &&
      !isProspect
    ) {
      setWalletNumberCallingRequest(walletNumberCallingRequest + 1);
      dispatch(retrieveHomeWalletSummary());
    }
  }, [dispatch, dossierFilter, myWalletCard, token, walletNumberCallingRequest]);

  useEffect(() => {
    if (
      token &&
      dossierNumberCallingRequest === 0 &&
      userType !== null &&
      !dossierFilter &&
      dossierFilterRequestStatus !== 'loading' &&
      !isProspect
    ) {
      setDossierNumberCallingRequest(dossierNumberCallingRequest + 1);
      dispatch(retrieveDossier());
    }
  }, [
    dispatch,
    setDossierNumberCallingRequest,
    dossierFilter,
    dossierFilterRequestStatus,
    userType,
    dossierNumberCallingRequest,
    token,
  ]);

  const onSelectedSection = useCallback(
    (sectionType: WalletSections) => {
      dispatch(homeActions.setSelectedWalletSection(sectionType));
      const myWalletNoData =
        sectionType === 'MY_WALLET' &&
        !myWalletCard?.value &&
        myWalletCard?.requestStatus !== 'loading';
      const sharedAccountsNoData =
        sectionType === 'SHARED_ACCOUNTS' &&
        !sharedAccountsCard?.value &&
        sharedAccountsCard?.requestStatus !== 'loading';

      if (
        (myWalletNoData || sharedAccountsNoData) &&
        dossierFilter &&
        dossierFilter.dossier.length > 0
      ) {
        dispatch(retrieveHomeWalletSummary());
      }
    },
    [dispatch, myWalletCard, sharedAccountsCard]
  );

  const setFlagShowBalance = useCallback(
    (showBalance: boolean) => {
      dispatch(updateShowBalance(showBalance));
    },
    [dispatch]
  );

  useEffect(() => {
    if (dossierFilterError) {
      const errorMessages = messagesForDossierError(dossierFilterError);
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(dossierSliceActions.clearDossierError());
    }
  }, [dispatch, dossierFilterError, closeToast, formatMessage, showToast]);

  const [specimenUploadDialogOpen, openSpecimenUploadDialog] = useState(false);

  /* questionnaire selection logic */
  const [confirmSignDialogOpen, setConfirmSignDialogOpen] = useState(false);
  const [mifidViewDialogOpen, setMifidViewDialogOpen] = useState(false);
  const [selectionDialogOpen, openSelectionDialog] = useState(false);
  const [startWebCollaborationDialogOpen, openStartWebCollaborationDialog] =
    useState(false);

  // sign button logic
  const openConfirmSignDialog = () => setConfirmSignDialogOpen(true);
  const closeConfirmSignDialog = () => setConfirmSignDialogOpen(false);

  const onConfirmSign = () => {
    closeConfirmSignDialog();
    dispatch(startSigningProcess())
      .unwrap()
      .then(() => {
        dispatch(retrieveOnboardingSelfPracticeData());
      });
  };

  // MIFID view dialog logic
  const openMifidViewDialog = () => setMifidViewDialogOpen(true);
  const closeMifidViewDialog = () => setMifidViewDialogOpen(false);

  const onMifidView = () => {
    closeMifidViewDialog();
    dispatch(resetSurvey());
    dispatch(resetMifidSurvey());
    navigate(appRoutes.MifidSurveyPage);
  };

  const onMifidModify = useCallback(() => {
    dispatch(
      updateQuestionnairesStatus({
        amlAndFactaCrsState: toDoActivities.value?.amlAndFatcaCrsState,
        mifidState: GetTasksResponseMifidStateEnum.ToComplete,
      })
    )
      .unwrap()
      .finally(() => {
        closeMifidViewDialog();
        dispatch(getTasks());
      });
  }, [toDoActivities.value?.amlAndFatcaCrsState, dispatch]);

  // refresh tasks when questionnaires status is updated
  useEffect(() => {
    if (updateQuestionnairesStatusStatus === 'succeeded') {
      dispatch(getTasks());
    }
  }, [updateQuestionnairesStatusStatus]);

  // navigate to sign page after signing process is started
  useEffect(() => {
    if (startSigningProcessRequestStatus === 'succeeded') {
      navigate(appRoutes.Sign);
      dispatch(signatureAreaActions.updateSelectedSection('TO_SIGN'));
    }
  }, [startSigningProcessRequestStatus]);

  const openDialog = useCallback(
    async (open: boolean) => {
      // Refresh data to get the latest status
      let practice = practiceData?.value;
      try {
        const freshData = await dispatch(
          retrieveOnboardingSelfPracticeData()
        ).unwrap();
        if (await freshData) {
          practice = freshData;
        }
      } catch (error) {
        console.error('Failed to retrieve fresh practice data', error);
      }

      const meetingDateStr = practice?.meetingDate;
      if (!meetingDateStr) {
        openSelectionDialog(open);
        return;
      }

      const today = new Date();

      const parsedMeetingDate = parseISO(meetingDateStr);
      const isToday = isSameDay(parsedMeetingDate, today);
      const isMeetingTimeReached = !isBefore(today, parsedMeetingDate);

      const shouldProceedDirectly =
        isToday &&
        isMeetingTimeReached &&
        (practice?.compileType === 'InPresenza'
          ? !!practice?.isMifidEnable
          : true);

      if (!shouldProceedDirectly) {
        openSelectionDialog(open);
      } else {
        if (practice?.compileType === 'InPresenza') {
          dispatch(resetUpdateQuestionnairesStatus());
          navigate(appRoutes.MifidSurveyPage);
        } else if (practice?.compileType === 'WebCollaboration') {
          dispatch(webCollaborationSliceActions.reset());
          openStartWebCollaborationDialog(true);
        }
      }
    },
    [
      practiceData?.value,
      dispatch,
      navigate,
      openStartWebCollaborationDialog,
      openSelectionDialog,
      toDoActivities.value?.mifidState,
    ]
  );

  useEffect(() => {
    if (roomId && startWebCollaborationDialogOpen) {
      openStartWebCollaborationDialog(false);
      navigate(`${appRoutes.MifidSurveyPage}/${roomId}`);
    }
  }, [roomId, startWebCollaborationDialogOpen]);

  const startWebCollaboration = useCallback(() => {
    dispatch(createRoom());
  }, []);
  const onCloseStartWebCollaborationDialog = useCallback(() => {
    openStartWebCollaborationDialog(false);
  }, []);

  const onModalitySelected = useCallback(
    (_type: Questionnaire /* future use */, mode?: SurveyBookingStatus) => {
      openSelectionDialog(false);
      dispatch(
        updateOnboardingCompileType({ compileType: mode, codicePratica })
      );
    },
    [codicePratica, dispatch]
  );

  useEffect(() => {
    if (updateOnboardingCompileTypeRequestStatus === 'succeded') {
      let descriptionLabel = '';
      if (compileTypeFromDb === 'WebCollaboration') {
        descriptionLabel = formatMessage(
          homeCompilationSelectionDialogMessages.successToastTextWebCollaboration,
          {
            customerName: name,
            cfName: financialAdvisorName,
          }
        );
      } else if (compileTypeFromDb === 'InPresenza') {
        descriptionLabel = formatMessage(
          homeCompilationSelectionDialogMessages.successToastTextPresence,
          {
            customerName: name,
            cfName: financialAdvisorName,
          }
        );
      }
      if (descriptionLabel) {
        showToast({
          type: 'success',
          title: formatMessage(
            homeCompilationSelectionDialogMessages.successToastTitle
          ),
          description: descriptionLabel,
          close: closeToast,
        });
      }
      dispatch(homeActions.clearUpdateOnboardingCompileTypeRequestStatus());
    } else if (updateOnboardingCompileTypeRequestStatus === 'failed') {
      showToast({
        type: 'error',
        title: formatMessage(commonErrorMessages.errorTitleDefault),
        description: formatMessage(commonErrorMessages.errorDescriptionDefault),
        close: closeToast,
      });
      dispatch(homeActions.clearUpdateOnboardingCompileTypeRequestStatus());
    }
  }, [
    updateOnboardingCompileTypeRequestStatus,
    compileTypeFromDb,
    formatMessage,
    name,
    financialAdvisorName,
    showToast,
    closeToast,
    dispatch,
  ]);

  const handleSpecimenUploadSuccess = useCallback(() => {
    const userString =
      user?.name && user?.surname ? `${user?.name} ${user.surname}` : '';
    setTimeout(() => {
      showToast({
        type: 'success',
        title: formatMessage(
          homeSpecimenUploadDialogMessages.successToastTitle
        ),
        description: formatMessage(
          homeSpecimenUploadDialogMessages.successToastMessage,
          { '0': userString }
        ),
        close: closeToast,
      });
      dispatch(getTasks());
    }, 200);
  }, [user, showToast, closeToast, formatMessage]);

  return {
    toDoActivities,
    myWallet,
    sharedAccounts,
    news,
    financialAdvisor,
    documents,
    signature,
    video,
    sections,
    selectedSection,
    dossierRequestStatus: dossierFilterRequestStatus,
    onSelectedSection,
    setFlagShowBalance,
    selectionDialogOpen,
    specimenUploadDialogOpen,
    openSpecimenUploadDialog,
    handleSpecimenUploadSuccess,
    confirmSignDialogOpen,
    openConfirmSignDialog,
    closeConfirmSignDialog,
    onConfirmSign,
    mifidViewDialogOpen,
    openMifidViewDialog,
    closeMifidViewDialog,
    onMifidView,
    onMifidModify,
    openDialog,
    onModalitySelected,
    practiceData,
    startWebCollaborationDialogOpen,
    startWebCollaboration,
    onCloseStartWebCollaborationDialog,
  };
};

const withShowBalance = (
  card: HomeCard<DashboardWalletSummary>,
  flagShowBalance: boolean
): HomeCard<DashboardWalletSummary> =>
  card?.value ? { ...card, value: { ...card.value, flagShowBalance } } : card;
